'use client';

import React, { useEffect, useState } from 'react';
import { fetchCommunityInsights } from '../utils/reviewerApi';
import type { CommunityInsight } from '../types';

interface VisitorInsightsProps {
  entityId: number;
}

/**
 * Approved visitor insights — text summarised from visitor YouTube videos by
 * the harvest pipeline, edited and approved by the site owner.
 *
 * Deliberately NOT rendered as a review card: no star rating, and every card
 * carries a "summarised from a visitor's video" source line so readers never
 * mistake it for a user-submitted review. Hides itself when empty, matching
 * the other community sections.
 */
const VisitorInsights: React.FC<VisitorInsightsProps> = ({ entityId }) => {
  const [insights, setInsights] = useState<CommunityInsight[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCommunityInsights(entityId).then((data) => {
      if (!cancelled) {
        setInsights(data.insights || []);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (!loaded || insights.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
        <span className="text-base">&#127909;</span>
        Visitor insights
      </h3>
      <div className="space-y-3">
        {insights.map((insight) => (
          <div
            key={insight.id}
            className="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-gray-50 p-5"
          >
            <p className="text-sm text-gray-700 leading-relaxed">{insight.text}</p>
            <p className="mt-3 text-[11px] text-gray-400 italic flex items-center gap-1">
              <span className="text-[10px]">&#9432;</span>
              Summarised from a visitor&rsquo;s video
              {insight.source_channel ? <> by {insight.source_channel}</> : null}
              {insight.source_video_url ? (
                <>
                  {' — '}
                  <a
                    href={insight.source_video_url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="underline hover:text-gray-600"
                  >
                    watch
                  </a>
                </>
              ) : null}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisitorInsights;
